import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";
import {
  categories,
  getCategory,
  symbolSlug,
  CATEGORY_SLUG_REDIRECTS,
} from "@/data/symbols";
import { getSymbolName, codepointLabel } from "@/data/symbol-names";

export const Route = createFileRoute("/collections/$slug")({
  loader: ({ params }) => {
    // Converged slugs: send legacy /collections/<old> to the canonical category.
    const moved = CATEGORY_SLUG_REDIRECTS[params.slug];
    if (moved) throw redirect({ to: "/categories/$slug", params: { slug: moved }, statusCode: 301 });
    const cat = getCategory(params.slug);
    if (!cat) throw notFound();
    const top = cat.symbols.slice(0, 10).map((s) => ({
      symbol: s,
      slug: symbolSlug(s),
      name: getSymbolName(s),
      code: codepointLabel(s),
    }));
    const others = categories.filter((c) => c.slug !== cat.slug).slice(0, 6);
    return { cat, top, others };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { cat } = loaderData;
    const title = `${cat.name} collection — ${cat.symbols.length} symbols to copy`;
    const description = `The complete ${cat.name.toLowerCase()} collection — ${cat.symbols.length} unique Unicode symbols, each with its own copy page, name and codepoint. ${cat.tagline}.`;
    const url = `/collections/${params.slug}`;
    // Canonicalize to /categories/$slug to resolve the categories vs collections duplication.
    const canonical = `/categories/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: abs(canonical) },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: abs(canonical) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${cat.name} Collection`,
            description,
            url,
            isPartOf: { "@type": "WebSite", name: "CoolSymbols.cool", url: "/" },
            about: {
              "@type": "DefinedTermSet",
              name: cat.name,
              url: `/categories/${cat.slug}`,
            },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: cat.symbols.length,
              itemListElement: cat.symbols.slice(0, 25).map((s, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: getSymbolName(s),
                url: `/symbol/${symbolSlug(s)}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
              { "@type": "ListItem", position: 2, name: "Collections", item: abs("/categories") },
              { "@type": "ListItem", position: 3, name: cat.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center p-8 text-center">
        <div>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Collection not found</h1>
          <Link to="/categories" className="mt-4 inline-block text-accent hover:underline">
            ← Browse all collections
          </Link>
        </div>
      </div>
      <SiteFooter />
    </div>
  ),
  component: CollectionPage,
});

function CollectionPage() {
  const { cat, top, others } = Route.useLoaderData();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <section className="mx-auto w-full max-w-5xl px-4 pt-8 sm:px-6 sm:pt-14">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/categories" className="hover:text-foreground">Collections</Link>
            <span>/</span>
            <span className="text-foreground">{cat.name}</span>
          </nav>

          <div className="glass overflow-hidden rounded-3xl p-5 sm:p-10">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
              <div className="text-5xl text-gradient sm:text-7xl">{cat.emoji}</div>
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  Collection · {cat.symbols.length} symbols
                </p>
                <h1 className="mt-1 break-words font-display text-2xl font-bold sm:text-4xl">
                  The complete {cat.name.toLowerCase()} collection
                </h1>
                <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                  {cat.description}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/categories/$slug"
                params={{ slug: cat.slug }}
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                Browse & copy →
              </Link>
              <a
                href="#top-symbols"
                className="inline-flex items-center justify-center rounded-md border border-border bg-surface/60 px-4 py-2 text-sm font-semibold hover:bg-secondary"
              >
                Top 10
              </a>
            </div>
          </div>
        </section>

        <section id="top-symbols" className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6">
          <h2 className="font-display text-lg font-semibold sm:text-2xl">
            Top 10 {cat.name.toLowerCase()}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
            The most-used symbols in this collection, each with their own page,
            name and Unicode codepoint.
          </p>
          <ol className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {top.map((s: { symbol: string; slug: string; name: string; code: string }, i: number) => (
              <li key={s.slug}>
                <Link
                  to="/symbol/$slug"
                  params={{ slug: s.slug }}
                  className="glass flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:border-primary/40"
                >
                  <span className="w-5 flex-none text-xs text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-xl">
                    {s.symbol}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{s.name}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {s.code}
                    </span>
                  </span>
                  <span className="text-muted-foreground">→</span>
                </Link>
              </li>
            ))}
          </ol>
        </section>

        <section className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6">
          <h2 className="font-display text-lg font-semibold sm:text-2xl">
            About this collection
          </h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/85 sm:text-base">
            <p>
              This page collects every {cat.name.toLowerCase().replace(" symbols", "")} symbol available on
              CoolSymbols.cool. Each symbol is a standard Unicode character —
              that means it works on the web, in social bios, in documents,
              in code editors and on every modern device, no fonts to install.
            </p>
            <p>
              Click any symbol to land on its dedicated page with the name,
              HTML entity, Unicode codepoint, related symbols and a one-click
              copy button.
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6">
          <h2 className="font-display text-lg font-semibold sm:text-2xl">
            Frequently asked
          </h2>
          <dl className="mt-4 space-y-3 text-sm sm:text-base">
            <div className="glass rounded-lg p-4">
              <dt className="font-semibold">Are these {cat.name.toLowerCase()} free to use?</dt>
              <dd className="mt-1 text-muted-foreground">
                Yes. All {cat.symbols.length} symbols are part of the public
                Unicode standard — free for any project, personal or commercial.
              </dd>
            </div>
            <div className="glass rounded-lg p-4">
              <dt className="font-semibold">How do I copy a symbol?</dt>
              <dd className="mt-1 text-muted-foreground">
                Tap any tile on the{" "}
                <Link to="/categories/$slug" params={{ slug: cat.slug }} className="text-accent hover:underline">
                  {cat.name.toLowerCase()} page
                </Link>{" "}
                — it's on your clipboard instantly.
              </dd>
            </div>
            <div className="glass rounded-lg p-4">
              <dt className="font-semibold">Will they display correctly everywhere?</dt>
              <dd className="mt-1 text-muted-foreground">
                Most {cat.name.toLowerCase()} render the same across modern
                browsers, iOS, Android, Windows and macOS. Older platforms may
                show a fallback glyph.
              </dd>
            </div>
          </dl>
        </section>

        <section className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <h2 className="font-display text-lg font-semibold sm:text-2xl">
            Other collections
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((c: { slug: string; name: string; tagline: string; emoji: string }) => (
              <Link
                key={c.slug}
                to="/collections/$slug"
                params={{ slug: c.slug }}
                className="glass flex items-center gap-3 rounded-xl p-4 transition-colors hover:border-primary/40"
              >
                <span className="text-2xl text-gradient">{c.emoji}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {c.tagline}
                  </span>
                </span>
                <span className="text-muted-foreground">→</span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
